import {
  OneWorkCardSkeleton,
  useHeaderHeightForScrollableContent,
  useWatchWindowResize,
} from '@diana/core';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

const LoaderIdPrefix = '$loader-';

export const CardPaddingTopBottom = 16;

export function useBoardParams<T extends object>(props: IUseBoardParamsProps<T>) {
  const { items, itemHeight, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = props;
  const headerHeight = useHeaderHeightForScrollableContent() - 10;
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const size = useWatchWindowResize({});
  const ref = useRef<HTMLDivElement>(null);

  const loaderItems = Array.from({ length: 5 }).map(
    (_, index) => ({ id: `${LoaderIdPrefix}${index}` } as unknown as T),
  );

  const allItems = isFetchingNextPage ? items.concat(loaderItems) : items;

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.offsetHeight);
      setWidth(ref.current.offsetWidth);
    }
  }, [size, isLoading]);

  const onScroll = useDebouncedCallback((e: any) => {
    const scrollTop = e.target.scrollTop;
    const scrollHeight = e.target.scrollHeight;
    const isCorrectScrollOffset = scrollTop + height >= scrollHeight - itemHeight * 2;
    if (hasNextPage && !isFetchingNextPage && isCorrectScrollOffset) {
      fetchNextPage();
    }
  }, 50);

  const isLoaderItem = (item: T & { id?: string }) => item?.id?.startsWith(LoaderIdPrefix);
  const renderLoaderItem = ({ index }: { index: number }) => (
    <OneWorkCardSkeleton
      key={`loader-${index}`}
      index={index}
      height={itemHeight - CardPaddingTopBottom}
    />
  );

  return {
    items: allItems,
    headerHeight,
    height,
    width,
    ref,
    onScroll,
    isLoaderItem,
    renderLoaderItem,
  };
}

export interface IUseBoardParamsProps<T extends object> {
  items: T[];
  itemHeight: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
}
